import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppInsightsContext, useTrackMetric } from '@microsoft/applicationinsights-react-js';
import useSelector from '@redux/typedHooks';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { actions as carsActions } from '@redux/modules/cars';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { UrlParamsType } from '@appTypes/urlParamsType';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { NotFound } from '@loadable-elements/NotFound';
import { Spinner } from '@components/spinner/Spinner';
import { DEFAULT_IMG_URL } from '@lib/constants';
import createAPIQuery from '@universal/lib/createURLQueries';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { ModelDetailPage } from '../Elbilguiden/ModelDetailPage/ModelDetailPage';

interface Props {
  carSlug: string;
}

const BilModell = ({ carSlug }: Props) => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'Bilmodell');
  const dispatch = useDispatch();
  const [isCarFetched, setIsCarFetched] = useState(false);
  const [isAdditionalModelsFetched, setIsAdditionalModelsFetched] = useState(false);
  const [didFetchMeta, setDidFetchMeta] = useState(false);
  const { loading: isTokenLoading, simpleToken } = useAuth0Token();

  const cars = useSelector((state) => state.cars?.data?.[0] || []);
  const car = useSelector((state) => state.cars?.mappedData?.[carSlug] || null);

  const carModelState = useMemo(() => car || { data: undefined, meta: { isUpdating: true } }, [car]);
  const isUpdating = carModelState?.meta?.isUpdating ?? true;

  const { name, brandName, meta, id, slug, chassis } = carModelState.data || {};

  const isElectric = useSelector(
    (state) => state.cars?.mappedData?.[slug]?.data?.fuelCategory?.includes('Elektrisk') || false,
  );

  useEffect(() => {
    if (cars?.length < 10 && !isAdditionalModelsFetched && chassis) {
      const searchParams: UrlParamsType = {
        filter: {
          chassis,
          fuelCategories: ['elektrisk'],
        },
      };
      const queries = createAPIQuery(searchParams);
      dispatch(carsActions.getCars.request(queries));
      setIsAdditionalModelsFetched(true);
    }
  }, [cars, dispatch, isAdditionalModelsFetched, chassis]);

  // Skip the car if `handleBilModell` already preloaded it.
  useEffect(() => {
    if (!isCarFetched) {
      if (!car?.meta?.fetchedAt) dispatch(carsActions.getOneCar.request(carSlug));
      setIsCarFetched(true);
    }
  }, [carSlug, isCarFetched, dispatch, car]);

  // The preloaded meta is fetched anonymously, so logged-in users refetch it.
  useEffect(() => {
    if (!didFetchMeta && carModelState?.data && slug && isElectric && !isTokenLoading) {
      if (!meta || simpleToken) dispatch(carsActions.getCarModelMeta.request(slug, { slug }));
      setDidFetchMeta(true);
    }
  }, [carModelState, didFetchMeta, dispatch, slug, isElectric, isTokenLoading, meta, simpleToken]);

  const seoDescription = meta?.ingress || `Informasjon om ${name || 'bilen'}.`;
  const seoTitle = meta?.seoConfig?.title || name || '';
  const preferredPicture = carModelState.data?.preferredPicture || null;
  const imgSrc = preferredPicture?.publicId || DEFAULT_IMG_URL;
  const windowPath = useWindowLocation();

  useSendGTMEventOnce(
    (carModelState?.data &&
      !isUpdating &&
      carModelState?.data?.fuelCategory &&
      carModelState?.data?.driveTrain && {
        event: NAFGTMEvent.bilmodell,
        motortype: carModelState?.data?.fuelCategory.join('/') || '',
        drivhjul: carModelState?.data?.driveTrain.join('/') || '',
      }) ||
      undefined,
    [carModelState],
  );

  if (!carModelState?.data && isCarFetched && !isUpdating) {
    return <NotFound />;
  }

  if (isUpdating) {
    return <Spinner />;
  }

  if (!isElectric && !isUpdating) {
    return <NotFound />;
  }

  return (
    <LayoutWithMainContent
      title={seoTitle || name || 'Bilguiden'}
      description={seoDescription}
      url={windowPath}
      imgUrl={imgSrc}
      gtmArgs={{
        page_type: 'CarModelPage',
        pageCategory: name ? `Bilguiden/${name}` : '',
        contentId: id || '',
      }}
      seoDescription={seoDescription}
      seoTitle={seoTitle}
      isGuidePage={isElectric}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Car,
        brandName,
        carModelImage: imgSrc,
      }}
    >
      <ModelDetailPage slug={carSlug} />
    </LayoutWithMainContent>
  );
};

export default BilModell;
